// An illustrated option in a grid of choices: picture above label,
// centered, a check badge in the corner that fills when chosen, a press
// that dips the tile and a hover that lifts its border. Transitions name
// their properties so the focus outline never animates.

import type { ReactNode } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";

import type { ChoiceTone } from "components/ChoiceCard/ChoiceCard";

const BASE =
  "relative flex w-full flex-col items-center gap-3 rounded-xl border-2 px-5 py-4 text-center text-lg font-medium transition-[background-color,border-color,transform] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cantelope/60 active:scale-[0.98] active:duration-100";

const BADGE =
  "absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors duration-200 sm:right-4 sm:top-4";

const SELECTED: Record<ChoiceTone, string> = {
  soil: "border-soil bg-soil text-white hover:bg-soil/90",
  daisy: "border-daisy-400 bg-daisy-100 text-soil hover:bg-daisy-50",
};

const IDLE =
  "border-oat-200 bg-white text-soil hover:border-soil/40 hover:bg-ivory/50";

export default function ChoiceTile({
  selected,
  onSelect,
  illustration,
  tone = "daisy",
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  /** Decorative — the label carries the meaning. */
  illustration?: string;
  tone?: ChoiceTone;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`${BASE} ${selected ? SELECTED[tone] : IDLE}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span
        aria-hidden="true"
        className={`${BADGE} ${
          selected
            ? "border-evergreen bg-evergreen"
            : "border-soil/30 bg-transparent"
        }`}
      >
        {selected && <CheckIcon className="h-5 w-5 text-oat" strokeWidth={3} />}
      </span>
      {illustration && (
        <img
          src={illustration}
          alt=""
          aria-hidden="true"
          className="h-14 w-14 object-contain"
        />
      )}
      <span>{children}</span>
    </button>
  );
}
